<template>
    <div class="p-3">
        <!-- Header -->
        <div class="mb-4">
            <h5 class="font-weight-bolder mb-1">
                <i class="fas fa-share-alt text-info me-2"></i>
                Compartir / Revocar Credenciales
            </h5>
            <p class="text-sm text-secondary mb-0">
                Selecciona un portal, una credencial y los usuarios a los que deseas asignarla o revocarla
            </p>
        </div>

        <!-- Portal selector -->
        <div class="mb-3">
            <label class="form-label fw-semibold text-sm">
                <i class="fas fa-globe text-primary me-2"></i>
                Portal <span class="text-danger">*</span>
            </label>
            <select v-model="selectedPortalId" class="form-select"
                :disabled="!!preSelectedPortal"
                @change="onPortalChange">
                <option value="" disabled>Selecciona un portal</option>
                <option v-for="p in portales" :key="p.id" :value="p.id">
                    {{ p.name }}
                </option>
            </select>
        </div>

        <!-- Credential selector (from endpoint 2) -->
        <div class="mb-3" v-if="selectedPortalId">
            <label class="form-label fw-semibold text-sm">
                <i class="fas fa-key text-warning me-2"></i>
                Credencial a compartir <span class="text-danger">*</span>
            </label>
            <div v-if="loadingPortalCredentials" class="text-center py-2">
                <i class="fas fa-spinner fa-spin text-primary"></i>
                <span class="text-sm text-secondary ms-2">Cargando credenciales...</span>
            </div>
            <select v-else v-model="selectedCredentialId" class="form-select"
                @change="onCredentialChange">
                <option value="" disabled>Selecciona una credencial</option>
                <option v-for="cred in portalCredentials" :key="getCredKey(cred)"
                    :value="getCredKey(cred)">
                    {{ cred.user?.name || '' }} {{ cred.user?.last_name || '' }}
                    ({{ cred.credential.email || cred.credential.user || cred.credential.username || 'Sin identificador' }})
                    - {{ cred.shared_count || 0 }} compartido(s)
                    {{ cred.is_oauth ? '[OAuth]' : '' }}
                </option>
            </select>
            <small v-if="portalCredentials.length === 0 && !loadingPortalCredentials"
                class="text-danger text-xs mt-1 d-block">
                No hay credenciales disponibles en este portal
            </small>
        </div>

        <!-- Shared users for selected credential -->
        <div class="mb-3" v-if="selectedCredential">
            <label class="form-label fw-semibold text-sm">
                <i class="fas fa-users-cog text-info me-2"></i>
                Usuarios con esta credencial asignada
                <span class="badge ms-1"
                    :class="selectedCredential.shared_count > 0 ? 'bg-gradient-info' : 'bg-secondary'"
                    style="font-size: 9px;">
                    {{ selectedCredential.shared_count || 0 }}
                </span>
            </label>
            <div class="border rounded shared-users-list" style="max-height: 120px; overflow-y: auto;">
                <div v-if="!selectedCredential.shared_users || selectedCredential.shared_users.length === 0"
                    class="text-center py-3 text-secondary">
                    <i class="fas fa-user-slash mb-1 d-block" style="font-size: 1.2rem; opacity: 0.4;"></i>
                    <small>Sin usuarios asignados aún</small>
                </div>
                <div v-else v-for="share in selectedCredential.shared_users" :key="share.share_id"
                    class="d-flex align-items-center justify-content-between p-2 border-bottom">
                    <div>
                        <span class="text-sm font-weight-bold">
                            {{ share.shared_with?.name || '' }} {{ share.shared_with?.last_name || '' }}
                        </span>
                        <br>
                        <small class="text-secondary">{{ share.shared_with?.email || '' }}</small>
                    </div>
                    <div class="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                        <small class="text-xxs text-secondary">
                            {{ share.shared_at ? formatDate(share.shared_at) : '' }}
                        </small>
                        <button type="button"
                            class="btn btn-sm btn-outline-danger p-0 px-1"
                            style="font-size: 10px; line-height: 1.4;"
                            :disabled="isLoading"
                            @click="handleDeleteShare(share.share_id)"
                            title="Eliminar asignación">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Search users -->
        <div class="mb-3" v-if="selectedPortalId && selectedCredentialId">
            <label class="form-label fw-semibold text-sm">
                <i class="fas fa-search text-secondary me-2"></i>
                Buscar usuario para asignar/revocar
            </label>
            <div class="d-flex gap-2">
                <input
                    v-model="searchQuery"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Nombre o email..."
                    autocomplete="off"
                    @keyup.enter="doSearch"
                />
                <button type="button" class="btn btn-sm btn-outline-primary flex-shrink-0"
                    @click="doSearch" :disabled="loadingMembers">
                    <i class="fas" :class="loadingMembers ? 'fa-spinner fa-spin' : 'fa-search'"></i>
                </button>
            </div>
        </div>

        <!-- User checkboxes -->
        <div class="mb-3" v-if="selectedPortalId && selectedCredentialId">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="form-label fw-semibold text-sm mb-0">
                    <i class="fas fa-users text-success me-2"></i>
                    Usuarios
                    <span class="text-xxs text-secondary fw-normal ms-1">
                        ({{ pagination.total }} encontrados)
                    </span>
                </label>
                <div>
                    <button type="button" class="btn btn-link text-xs p-0 me-3"
                        @click="selectAll">Todos</button>
                    <button type="button" class="btn btn-link text-xs p-0"
                        @click="deselectAll">Ninguno</button>
                </div>
            </div>

            <!-- Loading -->
            <div v-if="loadingMembers" class="text-center py-3 border rounded">
                <i class="fas fa-spinner fa-spin text-primary"></i>
                <span class="text-sm text-secondary ms-2">Buscando...</span>
            </div>

            <div v-else class="user-list border rounded" style="max-height: 250px; overflow-y: auto;">
                <div v-for="user in users" :key="user.id"
                    class="d-flex align-items-center p-2 border-bottom user-item"
                    :class="{
                        'bg-light': selectedUserIds.includes(user.id),
                        'already-shared': isAlreadyShared(user.id)
                    }">
                    <input
                        type="checkbox"
                        class="form-check-input me-3 flex-shrink-0"
                        :id="`assign-user-${user.id}`"
                        :value="user.id"
                        v-model="selectedUserIds"
                    />
                    <label :for="`assign-user-${user.id}`"
                        class="form-check-label flex-grow-1 mb-0" style="cursor: pointer;">
                        <span class="text-sm font-weight-bold">
                            {{ getUserName(user) }}
                        </span>
                        <span v-if="isAlreadyShared(user.id)"
                            class="badge bg-gradient-info ms-2" style="font-size: 9px;">
                            Ya asignado
                        </span>
                        <br>
                        <small class="text-secondary">{{ user.email }}</small>
                    </label>
                </div>
                <div v-if="users.length === 0" class="p-3 text-center text-secondary text-sm">
                    No se encontraron usuarios
                </div>
            </div>

            <!-- Pagination -->
            <div v-if="pagination.last_page > 1" class="d-flex justify-content-between align-items-center mt-2">
                <small class="text-secondary">
                    Pág. {{ pagination.current_page }} de {{ pagination.last_page }}
                </small>
                <div class="d-flex gap-1">
                    <button type="button" class="btn btn-sm btn-outline-secondary"
                        :disabled="pagination.current_page <= 1 || loadingMembers"
                        @click="changePage(pagination.current_page - 1)">
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-secondary"
                        :disabled="pagination.current_page >= pagination.last_page || loadingMembers"
                        @click="changePage(pagination.current_page + 1)">
                        <i class="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>

            <small class="text-secondary mt-1 d-block">
                {{ selectedUserIds.length }} usuario(s) seleccionado(s)
            </small>
        </div>

        <!-- Actions -->
        <div class="d-flex gap-2 justify-content-end mt-4 pt-3 border-top flex-wrap">
            <soft-button type="button" color="secondary" variant="outline" size="sm"
                @click="$emit('cancel')">
                <i class="fas fa-times me-1"></i> Cancelar
            </soft-button>
            <soft-button type="button" color="success" variant="gradient" size="sm"
                :disabled="!canSubmitAssign || isLoading"
                @click="handleAssign">
                <span v-if="isLoading">
                    <i class="fas fa-spinner fa-spin me-1"></i>
                </span>
                <span v-else>
                    <i class="fas fa-share me-1"></i> Compartir
                </span>
            </soft-button>
            <soft-button type="button" color="danger" variant="gradient" size="sm"
                :disabled="!canSubmitRevoke || isLoading"
                @click="handleUnassign">
                <span v-if="isLoading">
                    <i class="fas fa-spinner fa-spin me-1"></i>
                </span>
                <span v-else>
                    <i class="fas fa-ban me-1"></i> Revocar
                </span>
            </soft-button>
        </div>
    </div>
</template>

<script>
import { ref, computed, watchEffect } from "vue";
import SoftButton from "@/components/SoftButton.vue";

export default {
    name: "AssignModal",
    components: { SoftButton },
    props: {
        preSelectedPortal: { type: Object, default: null },
        users: { type: Array, required: true },
        portales: { type: Array, required: true },
        portalCredentials: { type: Array, default: () => [] },
        loadingPortalCredentials: { type: Boolean, default: false },
        pagination: { type: Object, default: () => ({ current_page: 1, last_page: 1, total: 0 }) },
        loadingMembers: { type: Boolean, default: false },
        isLoading: { type: Boolean, default: false },
    },
    emits: ['assign', 'unassign', 'delete-share', 'cancel', 'search', 'portal-change'],
    setup(props, { emit }) {
        const selectedPortalId = ref(props.preSelectedPortal?.id || '');
        const selectedCredentialId = ref('');
        const selectedUserIds = ref([]);
        const searchQuery = ref('');

        // Genera una clave única por credencial (las OAuth tienen id=null)
        const getCredKey = (cred) => {
            if (cred.credential.id !== null && cred.credential.id !== undefined) {
                return String(cred.credential.id);
            }
            return `oauth_${cred.user?.id || cred.credential.user_id}`;
        };

        // Credencial seleccionada con sus datos completos
        const selectedCredential = computed(() => {
            if (selectedCredentialId.value === '' || selectedCredentialId.value === null) return null;
            return props.portalCredentials.find(
                c => getCredKey(c) === String(selectedCredentialId.value)
            ) || null;
        });

        // IDs de usuarios que ya tienen esta credencial compartida
        const sharedUserIds = computed(() => {
            if (!selectedCredential.value?.shared_users) return [];
            return selectedCredential.value.shared_users.map(s => s.shared_with?.id).filter(Boolean);
        });

        const isAlreadyShared = (userId) => {
            return sharedUserIds.value.includes(userId);
        };

        // Compartir requiere portal + credencial + usuarios
        const canSubmitAssign = computed(() => {
            return selectedPortalId.value && selectedCredentialId.value && selectedUserIds.value.length > 0;
        });

        // Revocar solo requiere portal + usuarios
        const canSubmitRevoke = computed(() => {
            return selectedPortalId.value && selectedUserIds.value.length > 0;
        });

        const getUserName = (user) => {
            if (user.userdata) {
                const nombre = user.userdata.primer_nombre || '';
                const apellido = user.userdata.primer_apellido || '';
                if (nombre || apellido) return `${nombre} ${apellido}`.trim();
            }
            return user.email || `Usuario #${user.id}`;
        };

        const formatDate = (dateStr) => {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
        };

        const onPortalChange = () => {
            selectedCredentialId.value = '';
            selectedUserIds.value = [];
            emit('portal-change', selectedPortalId.value);
        };

        const onCredentialChange = () => {
            selectedUserIds.value = [];
            emit('search', buildFilters(), 1);
        };

        // Si ya viene un portal pre-seleccionado, cargar sus credenciales
        if (props.preSelectedPortal?.id) {
            emit('portal-change', props.preSelectedPortal.id);
        }

        const buildFilters = () => {
            const filters = {};
            const q = searchQuery.value.trim();
            if (q) {
                if (q.includes('@')) {
                    filters.email = q;
                } else {
                    filters.nameAgent = q;
                }
            }
            return filters;
        };

        const doSearch = () => {
            emit('search', buildFilters(), 1);
        };

        const changePage = (page) => {
            emit('search', buildFilters(), page);
        };

        const selectAll = () => {
            selectedUserIds.value = props.users.map(u => u.id);
        };

        const deselectAll = () => {
            selectedUserIds.value = [];
        };

        const handleAssign = () => {
            const cred = selectedCredential.value;
            emit(
                'assign',
                selectedPortalId.value,
                cred?.credential?.id,
                selectedUserIds.value,
                cred?.user?.id,
                cred?.is_oauth === true
            );
        };

        const handleUnassign = () => {
            emit('unassign', selectedPortalId.value, selectedUserIds.value);
        };

        const handleDeleteShare = (shareId) => {
            emit('delete-share', shareId, selectedPortalId.value);
        };

        // Auto-seleccionar primera credencial en cuanto carguen y no haya loading
        watchEffect(() => {
            if (
                !props.loadingPortalCredentials &&
                props.portalCredentials.length > 0 &&
                selectedCredentialId.value === ''  // solo si no hay ninguna seleccionada
            ) {
                selectedCredentialId.value = getCredKey(props.portalCredentials[0]);
                emit('search', {}, 1);
            }
        });

        return {
            selectedPortalId,
            selectedCredentialId,
            selectedUserIds,
            searchQuery,
            selectedCredential,
            getCredKey,
            isAlreadyShared,
            canSubmitAssign,
            canSubmitRevoke,
            getUserName,
            formatDate,
            onPortalChange,
            onCredentialChange,
            doSearch,
            changePage,
            selectAll,
            deselectAll,
            handleAssign,
            handleUnassign,
            handleDeleteShare,
        };
    }
};
</script>

<style scoped>
.user-item {
    transition: background-color 0.15s ease;
}
.user-item:hover {
    background-color: #f8f9fa;
}
.user-item.already-shared {
    background-color: #e8f4fd;
}
.shared-users-list .border-bottom:last-child {
    border-bottom: none !important;
}
.gap-1 {
    gap: 0.25rem;
}
.gap-2 {
    gap: 0.5rem;
}
</style>
